import { getBybitInstrumentInfo } from '@/shared/cex-sdk/exchanges/bybit/rest';
import { getKucoinSymbolsList } from '@/shared/cex-sdk/exchanges/kucoin/rest';
import { getMexcExchangeInfo } from '@/shared/cex-sdk/exchanges/mexc/rest';
import { proxyRestOptions } from '@/shared/cex-sdk/lib/proxy-client';

import { ECexName } from 'web3';

import {
  DEFAULT_SYMBOL_STATUS_TTL_MS,
  isEntryFresh,
  loadSymbolStatusCache,
  saveSymbolStatusCache,
  type SymbolStatusCacheEntries,
  type SymbolStatusCacheEntry,
} from './symbolStatusCacheStorage';

export type SymbolStatusRequestItem = {
  cex: ECexName;
  base: string;
  quote: string;
};

export type SymbolStatusMap = Record<string, { isSt: boolean }>;

export const buildSymbolStatusKey = (cex: string, quote: string, base: string) =>
  `${cex.toLowerCase()}-${quote.toUpperCase()}-${base.toUpperCase()}`;

const makeOkEntry = (isSt: boolean, updatedAt: number): SymbolStatusCacheEntry => ({
  isSt,
  updatedAt,
  status: 'ok',
});

const makeErrorEntry = (updatedAt: number): SymbolStatusCacheEntry => ({
  isSt: false,
  updatedAt,
  status: 'error',
});

const normalizeRequests = (items: SymbolStatusRequestItem[]) => {
  const unique = new Map<string, SymbolStatusRequestItem>();

  for (const item of items) {
    const cex = item.cex.toLowerCase() as ECexName;
    const base = item.base.toUpperCase();
    const quote = item.quote.toUpperCase();
    const key = buildSymbolStatusKey(cex, quote, base);

    if (!unique.has(key)) {
      unique.set(key, { cex, base, quote });
    }
  }

  return Array.from(unique.values());
};

const chunkArray = <T>(items: T[], chunkSize: number): T[][] => {
  if (chunkSize <= 0) {
    return [items];
  }

  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }
  return chunks;
};

const MEXC_BATCH_SIZE = 100;

const fetchMexcStatusEntries = async (
  requests: SymbolStatusRequestItem[],
  updatedAt: number,
): Promise<SymbolStatusCacheEntries> => {
  const entries: SymbolStatusCacheEntries = {};

  if (requests.length === 0) {
    return entries;
  }

  const normalized = requests.map(r => ({
    ...r,
    symbol: `${r.base}${r.quote}`.toUpperCase(),
  }));

  const chunks = chunkArray(normalized, MEXC_BATCH_SIZE);

  for (const chunk of chunks) {
    const symbols = chunk.map(r => r.symbol);

    try {
      const response = await getMexcExchangeInfo({ symbols }, proxyRestOptions);
      const stBySymbol = new Map(
        (response.symbols ?? []).map(s => [s.symbol, Boolean(s.st)] as const),
      );

      for (const { base, quote, cex, symbol } of chunk) {
        const key = buildSymbolStatusKey(cex, quote, base);
        // Missing symbol is treated as delisted → not ST
        const isSt = stBySymbol.has(symbol) ? Boolean(stBySymbol.get(symbol)) : false;
        entries[key] = makeOkEntry(isSt, updatedAt);
      }
    } catch (error) {
      for (const { base, quote, cex } of chunk) {
        const key = buildSymbolStatusKey(cex, quote, base);
        entries[key] = makeErrorEntry(updatedAt);
      }
    }
  }

  return entries;
};

const fetchKucoinStatusEntries = async (updatedAt: number): Promise<SymbolStatusCacheEntries> => {
  const response = await getKucoinSymbolsList(proxyRestOptions);
  const entries: SymbolStatusCacheEntries = {};

  for (const item of response.data ?? []) {
    if (!item?.baseCurrency || !item?.quoteCurrency) {
      continue;
    }

    const key = buildSymbolStatusKey(ECexName.kucoin, item.quoteCurrency, item.baseCurrency);
    entries[key] = makeOkEntry(Boolean(item.st), updatedAt);
  }

  return entries;
};

const fetchBybitStatusEntries = async (updatedAt: number): Promise<SymbolStatusCacheEntries> => {
  const entries: SymbolStatusCacheEntries = {};

  let cursor: string | undefined;
  let page = 0;
  const maxPages = 50;

  while (page < maxPages) {
    const response = await getBybitInstrumentInfo(
      { category: 'spot', limit: 1000, cursor },
      proxyRestOptions,
    );

    if (response.retCode !== 0) {
      throw new Error(response.retMsg || 'Bybit instruments-info request failed');
    }

    const list = response.result?.list ?? [];
    for (const item of list) {
      const baseCoin = item.baseCoin;
      const quoteCoin = item.quoteCoin;

      if (!baseCoin || !quoteCoin) {
        continue;
      }

      const key = buildSymbolStatusKey(ECexName.bybit, quoteCoin, baseCoin);
      entries[key] = makeOkEntry(item.stTag === '1', updatedAt);
    }

    cursor = response.result?.nextPageCursor || undefined;
    if (!cursor) {
      break;
    }

    page += 1;
  }

  return entries;
};

export const fetchCexSymbolStatuses = async (
  items: SymbolStatusRequestItem[],
): Promise<SymbolStatusMap> => {
  const normalized = normalizeRequests(items);
  const result: SymbolStatusMap = {};

  const byExchange = normalized.reduce<Record<ECexName, SymbolStatusRequestItem[]>>(
    (acc, item) => {
      acc[item.cex] = acc[item.cex] ? [...acc[item.cex], item] : [item];
      return acc;
    },
    {} as Record<ECexName, SymbolStatusRequestItem[]>,
  );

  const updatedAt = Date.now();

  for (const [exchange, requests] of Object.entries(byExchange) as Array<
    [ECexName, SymbolStatusRequestItem[]]
  >) {
    switch (exchange) {
      case ECexName.mexc: {
        const cached = await loadSymbolStatusCache(exchange);
        const entries: SymbolStatusCacheEntries = cached?.entries ?? {};

        const needsFetch: SymbolStatusRequestItem[] = [];

        for (const { base, quote, cex } of requests) {
          const key = buildSymbolStatusKey(cex, quote, base);
          const cachedEntry = entries[key];

          if (cachedEntry && isEntryFresh(cachedEntry, DEFAULT_SYMBOL_STATUS_TTL_MS)) {
            result[key] = { isSt: cachedEntry.isSt };
            continue;
          }

          needsFetch.push({ base, quote, cex });
        }

        if (needsFetch.length > 0) {
          const freshEntries = await fetchMexcStatusEntries(needsFetch, updatedAt);
          Object.assign(entries, freshEntries);
          await saveSymbolStatusCache(exchange, entries);

          for (const { base, quote, cex } of needsFetch) {
            const key = buildSymbolStatusKey(cex, quote, base);
            result[key] = { isSt: entries[key]?.isSt ?? false };
          }
        }
        break;
      }
      case ECexName.kucoin: {
        const cached = await loadSymbolStatusCache(exchange);
        const entries: SymbolStatusCacheEntries = cached?.entries ?? {};

        const needsFetch: SymbolStatusRequestItem[] = [];

        for (const { base, quote, cex } of requests) {
          const key = buildSymbolStatusKey(cex, quote, base);
          const cachedEntry = entries[key];

          if (cachedEntry && isEntryFresh(cachedEntry, DEFAULT_SYMBOL_STATUS_TTL_MS)) {
            result[key] = { isSt: cachedEntry.isSt };
            continue;
          }

          needsFetch.push({ base, quote, cex });
        }

        if (needsFetch.length > 0) {
          try {
            const freshEntries = await fetchKucoinStatusEntries(updatedAt);
            Object.assign(entries, freshEntries);

            // Cache delisted / missing requested symbols as not-ST
            for (const { base, quote, cex } of needsFetch) {
              const key = buildSymbolStatusKey(cex, quote, base);
              if (!entries[key]) {
                entries[key] = makeOkEntry(false, updatedAt);
              }
            }
          } catch (error) {
            for (const { base, quote, cex } of needsFetch) {
              const key = buildSymbolStatusKey(cex, quote, base);
              entries[key] = makeErrorEntry(updatedAt);
            }
          }

          await saveSymbolStatusCache(exchange, entries);

          for (const { base, quote, cex } of needsFetch) {
            const key = buildSymbolStatusKey(cex, quote, base);
            result[key] = { isSt: entries[key]?.isSt ?? false };
          }
        }
        break;
      }
      case ECexName.bybit: {
        const cached = await loadSymbolStatusCache(exchange);
        const entries: SymbolStatusCacheEntries = cached?.entries ?? {};

        const needsFetch: SymbolStatusRequestItem[] = [];

        for (const { base, quote, cex } of requests) {
          const key = buildSymbolStatusKey(cex, quote, base);
          const cachedEntry = entries[key];

          if (cachedEntry && isEntryFresh(cachedEntry, DEFAULT_SYMBOL_STATUS_TTL_MS)) {
            result[key] = { isSt: cachedEntry.isSt };
            continue;
          }

          needsFetch.push({ base, quote, cex });
        }

        if (needsFetch.length > 0) {
          try {
            const freshEntries = await fetchBybitStatusEntries(updatedAt);
            Object.assign(entries, freshEntries);

            // Cache missing requested symbols as not-ST
            for (const { base, quote, cex } of needsFetch) {
              const key = buildSymbolStatusKey(cex, quote, base);
              if (!entries[key]) {
                entries[key] = makeOkEntry(false, updatedAt);
              }
            }
          } catch (error) {
            for (const { base, quote, cex } of needsFetch) {
              const key = buildSymbolStatusKey(cex, quote, base);
              entries[key] = makeErrorEntry(updatedAt);
            }
          }

          await saveSymbolStatusCache(exchange, entries);

          for (const { base, quote, cex } of needsFetch) {
            const key = buildSymbolStatusKey(cex, quote, base);
            result[key] = { isSt: entries[key]?.isSt ?? false };
          }
        }
        break;
      }
      default: {
        for (const { base, quote, cex } of requests) {
          const key = buildSymbolStatusKey(cex, quote, base);
          result[key] = { isSt: false };
        }
      }
    }
  }

  return result;
};
